import Grid from '@mui/material/Grid';
import type { OptionsMap } from '@openx/types/options.ts';
import {
  type MetacategoryData,
  MetacategoryGroup,
  type MetacategoryGroupType,
} from '@openx/types/targeting/metacategory.ts';
import { AllowBlockType } from '@openx/types/targeting/targeting.ts';
import { Criteria } from '@openx/types/targeting/targetingValuesTypes.ts';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../../utils/context/TargetingContext.tsx';
import { KeywordsInput } from '../../shared/KeywordsInput/KeywordsInput.tsx';
import { OptionsDropdown } from '../../shared/OptionsDropdown/OptionsDropdown.tsx';
import { DrawerTopBox } from '../../shared/TargetingDrawer/DrawerTopBox.tsx';
import { METACATEGORY_ITEMS_LIMIT } from './constants.ts';
import { MetacategoryAllowBlockTypeDropdown } from './MetacategoryAllowBlockTypeDropdown.tsx';
import { MetacategoryBulkDrawer } from './MetacategoryBulkDrawer.tsx';
import { MetacategoryGroupOptionDropdown } from './MetacategoryGroupOptionDropdown.tsx';

type TopBoxProps = {
  items: MetacategoryData;
  onChange: (categories: MetacategoryData) => void;
  categoryType: MetacategoryGroupType;
  setCategoryType: (categoryType: MetacategoryGroupType) => void;
};

export const MetacategoryTopBox = ({ items, onChange, categoryType, setCategoryType }: TopBoxProps) => {
  const { t } = useTranslation();
  const { useMetacategoriesFetch } = useTargetingContext();

  const { areMetacategoriesLoading, metacategories, refetchMetacategories } = useMetacategoriesFetch();

  const [type, setType] = useState<AllowBlockType>(AllowBlockType.ALLOW);

  const availableOptions: OptionsMap = useMemo(() => {
    return Object.keys(metacategories).reduce((options, key) => {
      const isAllowing = type === AllowBlockType.ALLOW && !items.excludes?.includes(key);
      const isBlocking = type === AllowBlockType.BLOCK && !items.includes?.val.includes(key);

      if (isAllowing || isBlocking) {
        options[key] = metacategories[key];
      }

      return options;
    }, {} as OptionsMap);
  }, [items, metacategories, type]);

  const selectedOptions = useMemo(
    () => (type === AllowBlockType.ALLOW ? new Set(items.includes?.val) : new Set(items.excludes)),
    [items, type]
  );

  useEffect(() => {
    refetchMetacategories();
  }, [refetchMetacategories]);

  const renderedOption = useMemo(() => {
    switch (categoryType) {
      case MetacategoryGroup.ADDITIONAL_OPENX_CATEGORIES:
        return (
          <OptionsDropdown
            loading={areMetacategoriesLoading}
            options={availableOptions}
            onChange={values => {
              onChange({
                ...items,
                exclude_mfa: items.exclude_mfa ?? true,
                excludes: type === AllowBlockType.BLOCK ? Array.from(values) : items.excludes,
                includes:
                  type === AllowBlockType.ALLOW
                    ? values.size
                      ? {
                          op: items.includes?.op || Criteria.ANY,
                          val: type === AllowBlockType.ALLOW ? Array.from(values) : items.includes?.val || [],
                        }
                      : null
                    : items.includes,
              });
            }}
            selectedOptions={selectedOptions}
            placeholder={t('[ Select or Search OpenX Categories ]')}
            label={t('OpenX Categories')}
            dataTest="metacategory-input"
          />
        );
      case MetacategoryGroup.KEYWORDS:
        return (
          <KeywordsInput
            keywordsType={type}
            keywords={type === AllowBlockType.ALLOW ? items.keywords.includes : items.keywords.excludes}
            hiddenOptions={type === AllowBlockType.ALLOW ? items.keywords.excludes : items.keywords.includes}
            onChange={values => {
              onChange({
                ...items,
                keywords: {
                  excludes: type === AllowBlockType.BLOCK ? Array.from(values) : items.keywords.excludes,
                  includes: type === AllowBlockType.ALLOW ? Array.from(values) : items.keywords.includes,
                },
              });
            }}
            keywordsLimit={METACATEGORY_ITEMS_LIMIT}
          />
        );
    }
  }, [areMetacategoriesLoading, availableOptions, categoryType, items, onChange, selectedOptions, t, type]);

  return (
    <DrawerTopBox>
      <Grid
        container
        spacing={1}
        sx={{
          alignItems: 'flex-start',
          flexGrow: 1,
        }}
      >
        <Grid size={3}>
          <MetacategoryGroupOptionDropdown value={categoryType} onChange={v => setCategoryType(v)} />
        </Grid>
        <Grid size={3}>
          <MetacategoryAllowBlockTypeDropdown allowBlockType={type} setAllowBlockType={setType} />
        </Grid>
        <Grid size={6}>{renderedOption}</Grid>

        <MetacategoryBulkDrawer
          categoryType={categoryType}
          onChange={onChange}
          items={items}
          allowBlockType={type}
          setAllowBlockType={setType}
        />
      </Grid>
    </DrawerTopBox>
  );
};
